import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trophy, ArrowRight, Zap, Award, Download, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const EXPORT_API = process.env.REACT_APP_BACKEND_URL + '/api/export';

const BADGE_LABELS = {
  explorateur: 'Explorateur',
  premier_quiz: 'Premier Quiz',
  perfectionniste: 'Perfectionniste',
  apprenti: 'Apprenti',
  studieux: 'Studieux',
  expert: 'Expert',
  serie_feu: 'Série de Feu',
};

export default function Completion() {
  const { courseId } = useParams();
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [certLoading, setCertLoading] = useState(false);

  useEffect(() => {
    const complete = async () => {
      try {
        const [completionRes, courseRes] = await Promise.all([
          axios.post(`${API}/courses/${courseId}/complete`, {}, { withCredentials: true }),
          axios.get(`${API}/courses/${courseId}`, { withCredentials: true }),
        ]);
        setResult(completionRes.data);
        setCourse(courseRes.data);
        await refreshUser();
      } catch {
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    complete();
  }, [courseId, navigate, refreshUser]);

  const handleCertificate = async () => {
    setCertLoading(true);
    try {
      const resp = await fetch(`${EXPORT_API}/${courseId}/certificate`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Impossible de générer le certificat.');
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificat_kurso_${(course?.title || 'cours')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/gi, '_').slice(0, 30)
        .toLowerCase()}.pdf`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 150);
      toast.success('Certificat téléchargé !');
    } catch {
      toast.error('Erreur lors de la génération du certificat.');
    } finally {
      setCertLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0e17] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-[#ff6b6b] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0e17] flex flex-col items-center justify-center px-4 py-12">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#ffc107]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-[#20c997]/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-sm w-full text-center space-y-6 relative">
        {/* Trophy */}
        <div className="animate-fade-up">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-[#ffc107]/20 to-[#ff6b6b]/20 border border-[#ffc107]/30 flex items-center justify-center mb-4">
            <Trophy size={44} className="text-[#ffc107]" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-white mb-2">{t('completion.title')}</h1>
          <p className="text-[#e8e8e8] text-base">{t('completion.subtitle')}</p>
        </div>

        {/* Course name */}
        {course && (
          <div className="kurso-card p-4 animate-fade-up" style={{ animationDelay: '0.1s' }}>
            <p className="text-white font-semibold text-lg font-heading">{course.title}</p>
          </div>
        )}

        {/* XP */}
        {result?.xp_gained > 0 && (
          <div className="animate-fade-up bg-[#ffc107]/10 border border-[#ffc107]/20 rounded-2xl p-5" style={{ animationDelay: '0.2s' }}>
            <div className="flex items-center justify-center gap-2 mb-1">
              <Zap size={20} className="text-[#ffc107]" />
              <span className="font-heading text-2xl font-bold text-[#ffc107]">+{result.xp_gained} XP</span>
            </div>
            <p className="text-[#e8e8e8] text-sm">{t('completion.xpEarnedForCourse')}</p>
            <p className="text-white font-semibold mt-1">{result.total_xp} XP total</p>
          </div>
        )}

        {/* New badges */}
        {result?.new_badges?.length > 0 && (
          <div className="animate-fade-up" style={{ animationDelay: '0.3s' }}>
            <p className="text-[#e8e8e8] text-sm mb-3">{t('completion.newBadges')}</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {result.new_badges.map(badge => (
                <span key={badge} className="xp-badge px-3 py-1.5 text-sm flex items-center gap-1.5">
                  <Award size={12} />
                  {BADGE_LABELS[badge] || badge}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3 animate-fade-up" style={{ animationDelay: '0.4s' }}>
          {/* Certificate */}
          <button
            onClick={handleCertificate}
            disabled={certLoading}
            data-testid="download-certificate-btn"
            className="w-full py-3.5 rounded-2xl bg-[#ffc107]/10 border border-[#ffc107]/30 text-[#ffc107] font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#ffc107]/20 transition-colors disabled:opacity-60"
          >
            {certLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            Télécharger mon certificat
          </button>

          <button
            onClick={() => navigate('/dashboard')}
            className="btn-primary w-full py-4 text-base"
            data-testid="completion-dashboard-btn"
          >
            {t('completion.backToDashboard')} <ArrowRight size={18} className="inline ml-2" />
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-secondary w-full py-3.5 text-sm"
            data-testid="completion-explore-btn"
          >
            {t('completion.continueLearning')}
          </button>
        </div>
      </div>
    </div>
  );
}
